/**
 * Native in-app-purchase wrapper around RevenueCat. Web stays on
 * Stripe Checkout (see billing.ts) — RevenueCat takes over on iOS
 * (StoreKit) and Android (Google Play Billing).
 *
 * RevenueCat is the source of truth for subscription state on
 * native platforms. The flow:
 *   1. App boot → initIAP() → Purchases.configure() with the
 *      platform-specific public API key.
 *   2. After auth → bindIAPUser(supabaseUserId) so RC subscriber
 *      identity matches our auth user.
 *   3. User taps Subscribe → fetchOfferings() → purchasePackage()
 *      → StoreKit / Play Billing sheet → on success, RC's webhook
 *      hits our Supabase edge function and updates
 *      `email_subscriptions`.
 *   4. App can also call hasActiveEntitlement() locally for an
 *      instant client-side answer that doesn't wait for the
 *      webhook round-trip.
 *
 * Public API keys are safe to ship in the client — that's RC's
 * design. Set VITE_REVENUECAT_PUBLIC_KEY_IOS and
 * VITE_REVENUECAT_PUBLIC_KEY_ANDROID in env. We use Vite env on
 * web too because the same module loads everywhere; the no-op
 * branch returns early before the keys are read.
 */
import { Capacitor } from '@capacitor/core'
// Static import is required: the dynamic `import('@revenuecat/...')`
// chunk hangs silently in Capacitor's iOS WebView, so RC never
// initialises and the paywall errors with "products are still being
// set up". Web bundle gets a small unused chunk; the early return
// below in initIAP() means it's harmless on non-native.
import { Purchases } from '@revenuecat/purchases-capacitor'

const ENTITLEMENT_ID = 'all_access'

export type IAPPlatform = 'ios' | 'android' | 'web'

export function iapPlatform(): IAPPlatform {
  if (!Capacitor.isNativePlatform()) return 'web'
  const p = Capacitor.getPlatform()
  if (p === 'ios') return 'ios'
  if (p === 'android') return 'android'
  return 'web'
}

export function isNativeIAPPlatform(): boolean {
  const p = iapPlatform()
  return p === 'ios' || p === 'android'
}

let configured = false

export async function initIAP(): Promise<void> {
  if (!isNativeIAPPlatform()) return
  if (configured) return
  const apiKey = pickPlatformKey()
  if (!apiKey) {
    console.warn('[iap] RevenueCat key missing, IAP disabled on this build')
    return
  }
  await Purchases.configure({ apiKey })
  configured = true
}

/** Tie the RC subscriber to our Supabase auth user so the webhook
 *  can reach back into our DB by the same id. Idempotent. */
export async function bindIAPUser(supabaseUserId: string): Promise<void> {
  if (!isNativeIAPPlatform() || !configured) return
  await Purchases.logIn({ appUserID: supabaseUserId })
}

/** Drop the RC subscriber link on sign-out so a shared device
 *  doesn't leak entitlements to the next account. */
export async function unbindIAPUser(): Promise<void> {
  if (!isNativeIAPPlatform() || !configured) return
  await Purchases.logOut()
}

export interface IAPPackage {
  identifier: string
  /** "yearly" | "monthly" — derived from RC's package type. */
  plan: 'yearly' | 'monthly'
  priceString: string
  /** ISO duration of the intro free trial, e.g. "P3D". Null when no
   *  trial is offered (returning users who already burned theirs). */
  trialIso: string | null
}

/** Pull the available subscription packages from RC. Empty when not
 *  on a native platform or RC isn't configured (e.g. dev w/o keys). */
export async function fetchOfferings(): Promise<IAPPackage[]> {
  if (!isNativeIAPPlatform() || !configured) return []
  const { current } = await withTimeout(Purchases.getOfferings(), 15_000, 'Loading subscriptions')
  if (!current) return []
  return current.availablePackages.map(pkg => ({
    identifier: pkg.identifier,
    plan: pkg.packageType === 'ANNUAL' ? 'yearly' : 'monthly',
    priceString: pkg.product.priceString,
    trialIso: pkg.product.introPrice?.periodNumberOfUnits
      ? `P${pkg.product.introPrice.periodNumberOfUnits}${pkg.product.introPrice.periodUnit?.[0] ?? 'D'}`
      : null,
  }))
}

/** Reject after `ms` so a StoreKit call that never settles (sandbox
 *  sessions are notorious for this) can't strand the UI on a spinner.
 *  If the sheet DOES complete after we bail, RC's webhook still lands
 *  the entitlement — Restore Purchases or an app restart picks it up. */
function withTimeout<T>(p: Promise<T>, ms: number, what: string): Promise<T> {
  return Promise.race([
    p,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(
        `${what} is taking too long. If you completed the purchase it will unlock shortly. Try Restore Purchases. Otherwise please try again.`,
      )), ms),
    ),
  ])
}

/** Trigger the StoreKit / Play Billing sheet for a package id pulled
 *  from fetchOfferings. Resolves on successful purchase, throws if
 *  the user cancels or RC reports an error. */
export async function purchasePackage(packageIdentifier: string): Promise<void> {
  if (!isNativeIAPPlatform() || !configured) {
    throw new Error('In-app purchase is only available on the iOS / Android app.')
  }
  const offerings = await withTimeout(Purchases.getOfferings(), 15_000, 'Loading subscriptions')
  const pkg = offerings.current?.availablePackages.find(
    p => p.identifier === packageIdentifier,
  )
  if (!pkg) throw new Error('Package not found')
  await withTimeout(Purchases.purchasePackage({ aPackage: pkg }), 90_000, 'The App Store purchase sheet')
  // Webhook will sync the row to email_subscriptions; the local
  // useSubscription hook re-fetches on session change anyway.
}

/** Cheap client-side check — hits RC's local cache, not the API,
 *  so it's safe to call repeatedly. The DB row (set by the webhook)
 *  is the durable truth. */
export async function hasActiveEntitlement(): Promise<boolean> {
  if (!isNativeIAPPlatform() || !configured) return false
  const { customerInfo } = await Purchases.getCustomerInfo()
  return Boolean(customerInfo.entitlements.active[ENTITLEMENT_ID])
}

/** "Restore Purchases" button — refreshes RC's view of what the
 *  underlying store thinks the user owns. Used when a user
 *  reinstalls and their entitlement isn't reflected yet. */
export async function restorePurchases(): Promise<void> {
  if (!isNativeIAPPlatform() || !configured) return
  await Purchases.restorePurchases()
}

/** Equivalent of openCustomerPortal() on web — opens the OS-level
 *  subscription management screen. Apple/Google hold the management
 *  UI for native subs (we don't get to host it). */
export async function openNativeSubscriptionManagement(): Promise<void> {
  const platform = iapPlatform()
  if (platform === 'ios') {
    // Apple-recommended deep link to Settings → Apple ID → Subscriptions.
    window.location.href = 'https://apps.apple.com/account/subscriptions'
  } else if (platform === 'android') {
    // Google Play subscriptions deep link. The package query string is
    // optional but lets the user land directly on our app's
    // subscription page if they have multiple subs.
    window.location.href =
      'https://play.google.com/store/account/subscriptions?package=com.jordandraper.games'
  }
}

// Publishable RevenueCat SDK key — safe to embed in the client by
// RC's design (same rationale as the Supabase anon-key fallback in
// supabase.ts). Hardcoded as a fallback because Xcode Cloud builds
// ship an empty .env: without this, iap had no key, RC never
// configured, and the paywall got stuck on "products are still being
// set up". Local dev still reads from .env. If the key is ever
// rotated, update both places (.env + here). Android stays env-only
// until that key is real (it's still a placeholder).
const PROD_REVENUECAT_KEY_IOS = 'appl_tlAKJjlObDbIquojoGYJuYLLGRH'

function pickPlatformKey(): string | undefined {
  const p = iapPlatform()
  if (p === 'ios')     return (import.meta.env.VITE_REVENUECAT_PUBLIC_KEY_IOS as string | undefined) || PROD_REVENUECAT_KEY_IOS
  if (p === 'android') return import.meta.env.VITE_REVENUECAT_PUBLIC_KEY_ANDROID as string | undefined
  return undefined
}
